import type { FC } from 'react';
import React from 'react';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';

import { Favorite, Pets, Search } from '@rover/icons';
import { Flex } from '@rover/kibble/core';
import BrandLink from '@rover/react-lib/src/components/typography/BrandLink';
import { LEFT_NAV_BAR_ITEMS } from '@rover/react-lib/src/constants/navBar.constants';
import usePageContext from '@rover/react-lib/src/context/usePageContext';
import TrainingUrls from '@rover/react-lib/src/pages/training/constants/urls';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import saveNavigationEvent from '@rover/utilities/saveNavigationEvent';

import ReferLink from './links/ReferLink';
import NavBarItem from './NavBarItem';

export type Props = {
  isOurServicesDropdownOpen: boolean;
  onToggleOurServices: () => void;
};

const NavBarLeft: FC<Props> = ({ onToggleOurServices, isOurServicesDropdownOpen }) => {
  const { navigationUrls } = useCountryConfigurationShim();
  const { isActiveServiceProvider, isAuthenticated } = useCurrentUserShim();

  const {
    leftNavBarItems = [
      LEFT_NAV_BAR_ITEMS.SEARCH_SITTERS_LINK,
      LEFT_NAV_BAR_ITEMS.BECOME_A_SITTER_LINK,
      LEFT_NAV_BAR_ITEMS.REFER_LINK,
      LEFT_NAV_BAR_ITEMS.OUR_SERVICES_LINK,
    ],
  } = usePageContext();

  if (!navigationUrls) {
    return null;
  }
  return (
    <Flex noWrap gap="4x" alignItems="center">
      <BrandLink />

      {leftNavBarItems.map((ItemToDisplay, idx) => {
        if (typeof ItemToDisplay !== 'string') {
          return <ItemToDisplay key={idx} />;
        }

        switch (ItemToDisplay) {
          case LEFT_NAV_BAR_ITEMS.SEARCH_SITTERS_LINK:
            return (
              <NavBarItem
                key={ItemToDisplay}
                href="/search/"
                icon={Search}
                display={['none', 'block']}
              >
                <Trans id="Search Sitters" />
              </NavBarItem>
            );

          case LEFT_NAV_BAR_ITEMS.SEARCH_TRAINERS_LINK:
            return (
              <NavBarItem
                key={ItemToDisplay}
                href={TrainingUrls.SEARCH}
                icon={Search}
                display={['none', 'block']}
              >
                {/* TODO (DEV-104195): Translate before going international */}
                Search Trainers
              </NavBarItem>
            );

          case LEFT_NAV_BAR_ITEMS.BECOME_A_SITTER_LINK:
            return (
              !isActiveServiceProvider && (
                <NavBarItem
                  key={ItemToDisplay}
                  onClick={() => {
                    saveNavigationEvent({
                      event: 'navigation-become-a-sitter',
                    });
                  }}
                  href={navigationUrls.becomeASitterUrl}
                  icon={Favorite}
                  aria-label={t`Become a Sitter`}
                  display={['none', 'none', 'block']}
                >
                  <Trans id="Become a Sitter" />
                </NavBarItem>
              )
            );

          case LEFT_NAV_BAR_ITEMS.REFER_LINK:
            return isAuthenticated && <ReferLink key={ItemToDisplay} />;

          case LEFT_NAV_BAR_ITEMS.OUR_SERVICES_LINK:
            return (
              !isAuthenticated && (
                <NavBarItem
                  key={ItemToDisplay}
                  display={['none', 'none', 'block']}
                  onClick={onToggleOurServices}
                  aria-expanded={isOurServicesDropdownOpen}
                  icon={Pets}
                >
                  <Trans id="Our Services" />
                </NavBarItem>
              )
            );

          default:
            return null;
        }
      })}
    </Flex>
  );
};

export default NavBarLeft;
